<template>
  <div>
    <div class="ts-flex ts-justify-between">
      <h1 class="page-header">{{ $t('workSchedule.workSchedule') }}</h1>
    </div>
    <ts-card>
      <ts-loading-banner :loading="!shifts.length && ready_fetch">
        <ts-card-wrapper>
          <form class="form-horizontal">
            <div class="ts-flex">
              <div class="ts-flex">
                <label class="col-sm-4 control-label">{{ $t('workSchedule.year') }}</label>
                <div>
                  <ts-date-picker-input
                    :class="{ 'is-invalid': errors.has('cycle_year') }"
                    format="YYYY"
                    v-model="model.cycle_year"
                    @input="fetchData"
                  ></ts-date-picker-input>
                </div>
                <div
                  class="invalid-feedback"
                  v-if="errors.has('cycle_year')"
                >{{ errors.first('cycle_year') }}</div>
              </div>
              <div class="ts-flex">
                <label class="col-sm-4 control-label">{{ $t('workSchedule.month') }}</label>
                <div>
                  <ts-date-picker-input
                    :class="{ 'is-invalid': errors.has('cycle_month') }"
                    format="MM"
                    v-model="model.cycle_month"
                    @input="fetchData"
                  ></ts-date-picker-input>
                </div>
                <div
                  class="invalid-feedback"
                  v-if="errors.has('cycle_month')"
                >{{ errors.first('cycle_month') }}</div>
              </div>
              <div class="ts-flex">
                <label class="col-sm-4 control-label required">{{ $t('workSchedule.shift') }}</label>
                <div>
                  <select
                    style="width: 150%"
                    :class="{ 'is-invalid': errors.has('shift_id') }"
                    v-model="model.shift_id"
                    class="form-control"
                    @change="fetchData"
                  >
                    <option value disabled selected style="display: none;">--{{ $t('select') }}--</option>
                    <option
                      v-for="(shift, index) in shifts"
                      :key="index"
                      :value="shift.shift_id"
                    >{{ shift.shift_name }} ({{ shift.shift_type }})</option>
                  </select>
                </div>
                <div
                  class="invalid-feedback"
                  v-if="errors.has('shift_id')"
                >{{ errors.first('shift_id') }}</div>
              </div>
              <div class="ts-flex-grow"></div>
              <b-dropdown
                :text="$t('button.export')"
                variant="outline-default"
                class="mr-2"
                :waiting="loading"
                :disabled="details.length <= 0 || loading"
              >
                <b-dropdown-item href="#" @click="exportExcel">
                  <i class="fas fa-file-excel ts-text-green-500"></i>
                  {{ $t('button.excel') }}
                </b-dropdown-item>
                <b-dropdown-item href="#" disabled @click="exportPdf">
                  <i class="fas fa-file-pdf ts-text-red-500"></i>
                  {{ $t('button.pdf') }}
                </b-dropdown-item>
              </b-dropdown>
              <ts-button
                color="success"
                outline
                class="mr-2"
                @click.prevent="showCopyForm"
              >{{ $t('button.copy') }}</ts-button>
              <ts-button
                class="btn-primary"
                :disabled="details.length <= 0"
                @click.prevent="save"
                :waiting="saving"
              >{{ $t('button.save') }}</ts-button>
            </div>
          </form>
          <ts-loading-banner :loading="loading">
            <div class="menu-container">
              <div class="schedule-table-container">
                <table class="table table-bordered" v-if="details.length > 0 && !loading">
                  <thead>
                    <tr>
                      <th hidden>Staff ID</th>
                      <th>{{ $t('workSchedule.employeeId') }}</th>
                      <th>{{ $t('workSchedule.employeeName') }}</th>
                      <th
                        style="vertical-align: middle;"
                        v-for="(column, index) in header"
                        :key="index"
                      >{{ subString(column) }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(data, index) in details" :key="index">
                      <td hidden>{{ data.employee_id }}</td>
                      <td>{{ data.card_id }}</td>
                      <td>{{ data.staff_name }}</td>
                      <td v-for="(column, columnIndex) in header" :key="columnIndex">
                        <select class="form-control border-none" v-model="data[column]">
                          <option v-if="data[column] === null" :value="null">-</option>
                          <option v-else value>-</option>
                          <option
                            v-for="(special_keyword, index) in special_keywords"
                            :key="index"
                            :value="special_keyword.keyword"
                          >{{ special_keyword.keyword }}</option>
                        </select>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </ts-loading-banner>
        </ts-card-wrapper>
      </ts-loading-banner>
    </ts-card>
    <ul class="list-group list-group-flush col-md-3">
      <li v-for="(special_keyword, index) in special_keywords" :key="index" class="list-group-item">
        <span>{{ special_keyword.keyword }}</span>
        <p class="pull-right text-primary">{{ special_keyword.remarks }}</p>
      </li>
    </ul>
  </div>
</template>

<script>
import { LOAD_ROOT_OPTIONS } from '@riophae/vue-treeselect'
import { mapState, mapActions } from 'vuex'
import { debounce } from 'lodash'
import moment from 'moment'
import * as FileDownload from 'downloadjs'
import { Errors } from 'form-backend-validation'
import PreviewPdf from './preview-pdf'
import CopyForm from './copy-form'
import { trim } from 'lodash'

export default {
  name: 'work-schedule-form',
  components: {
    PreviewPdf,
    CopyForm
  },
  data() {
    const newModel = {
      cycle_year: moment().format('YYYY'),
      cycle_month: moment().format('MM'),
      shift_id: ''
    }

    return {
      newModel: newModel,
      model: _.cloneDeep(newModel),
      saving: false,
      header: [],
      details: [],
      loading: false,
      ready_fetch: false,
      exporting: false,
      errors: new Errors()
    }
  },
  computed: {
    ...mapState('hr/workSchedule', ['shifts', 'special_keywords'])
  },
  created() {
    this.fetchResourceBeforeCreate()
  },
  methods: {
    subString(string) {
      if (string === '' || string === undefined || string === null) return
      return string.substr(2)
    },

    fetchResourceBeforeCreate() {
      this.ready_fetch = true
      this.$store
        .dispatch('hr/workSchedule/dataBeforeCreate')
        .then(() => {
          this.ready_fetch = false
        })
        .catch(error => {
          this.ready_fetch = false
          this.$toasted.error(error.message)
        })
    },
    getSchedule() {
      this.loading = true
      this.$store
        .dispatch('hr/workSchedule/fetch', this.model)
        .then(response => {
          this.header = response.header
          this.details = response.data
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
    },
    async fetchData() {
      if (this.model.cycle_year == '' || this.model.cycle_month == '' || this.model.shift_id == '') return
      this.loading = true
      this.employeeOptions = null
      await this.$store.dispatch('hr/workSchedule/getSpecialKeyword', this.model.shift_id)
      await this.getSchedule()
    },
    async save() {
      this.errors = new Errors()
      this.saving = true

      try {
        let response = await this.$store.dispatch(
          'hr/workSchedule/store',
          Object.assign({}, this.model, { details: this.details })
        )

        this.$toasted.success(response.message)
      } catch (error) {
        this.$toasted.error(error.message)
        this.errors = new Errors(error.errors)
      }

      this.saving = false
    },
    showCopyForm(editData) {
      this.$modal.show(
        CopyForm,
        {
          editData: editData
        },
        {
          height: 'auto',
          width: '500px',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        }
      )
    },
    exportExcel() {
      this.loading = true

      this.$store
        .dispatch('hr/workSchedule/exportExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(response.headers['content-disposition'].indexOf('filename'))
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(String.fromCharCode.apply(null, new Uint8Array(err)))
          this.$toasted.error(error.message)
        })
        .finally(() => (this.loading = false))
    },
    exportPdf() {
      this.$modal.show(
        PreviewPdf,
        {
          org_chart_structure_id: this.org_chart_structure_id,
          cycle_year: this.cycle_year
        },
        {
          height: 'auto',
          width: '70%',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        }
      )
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('hr/workSchedule/RESET_STATE')
    next()
  }
}
</script>
<style lang="scss" scoped>
.menu-container {
  margin-top: 16px;
  background-color: #fff;
  border: solid 1px #d3d8df;
  height: 56vh;
  overflow-y: scroll;
  overflow-x: scroll;
}
.schedule-table-container {
  height: 54vh;
  width: 100px;

  .table > thead > tr > th {
    position: sticky;
    top: 0;
    background: rgba(230, 230, 230, 1);
    z-index: 10;
  }
  table th,
  td {
    padding: 2px 4px;
    vertical-align: middle !important;
    white-space: nowrap;
  }
  .border-none {
    border: none;
    background: #fff;
    width: 45px;
    font-size: 11px;
    outline: none;
  }
}
::-webkit-scrollbar {
  height: 10px;
  width: 3px; /* Remove scrollbar space */
  background: transparent; /* Optional: just make scrollbar invisible */
}

/* Optional: show position indicator in red */
::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: #dee0e0;
}
div::-webkit-scrollbar-thumb:hover {
  background: #bebfc0;
}
/* This is to remove the arrow of select element in IE */
select::-ms-expand {
  display: none;
}
select {
  -webkit-appearance: none;
  appearance: none;
  text-align: center;
  text-align-last: center;
}

option {
  text-align: left;
}
</style>
